import { authSlice } from '@wellesley/auth'
import { useSelector } from 'react-redux'
import { PropsWithChildren, useCallback, useMemo } from 'react'
import { TooltipWrapper as TWrapper } from '@wellesley/ui'
import { getDisplayUsername } from '@wellesley/user/helpers/getDisplayUsername'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import { userSlice } from '@wellesley/user/slices/userSlice'
import { askLogin } from '@wellesley/host-remote/api'

type Action = () => void | Promise<void>

const useActionGuard = (guestAction?: () => void) => {
  const user = useSelector(userSlice.selectors.selectCurrentUser)
  const isLogged = useSelector(authSlice.selectors.selectIsAuthenticated)

  const isRestricted = user?.state === 'SUSPENDED' //todo: handle other non-regular cases
  const isDisabled = isRestricted

  const ActionTooltipWrapper = useMemo(() => {
    const innerCmp = ({
      children,
      triggerClassname,
    }: PropsWithChildren<{ triggerClassname?: string }>) => {
      if (!isDisabled) return <>{children}</>

      return (
        <TWrapper
          theme="dark"
          tooltipText={`${getDisplayUsername(getUserNameWithDomain(user))} is suspended`}
          size="default"
          triggerClassname={triggerClassname}
        >
          <div className="!pointer-events-auto w-full">{children}</div>
        </TWrapper>
      )
    }
    innerCmp.displayName = 'ActionTooltipWrapper'
    return innerCmp
  }, [isDisabled, user])

  const guardedAction = useCallback(
    (action: Action) => () => {
      if (!isLogged) {
        if (guestAction) guestAction()
        else askLogin()
      } else if (isLogged && !isRestricted) {
        void action()
      }
    },
    [isLogged, isRestricted, guestAction],
  )

  return { isDisabled, guardedAction, ActionTooltipWrapper }
}

export default useActionGuard
